// Browser-side OCR pipeline. Replaces the server-side /api/ocr/crop-multi
// route so the user's machine does the rendering + Tesseract work — Render's
// 2 GB / 4 GB RAM limits stop being a bottleneck.
//
// Mirrors the server's pipeline (server/routes/ocr.js):
//   - Load sheet PDF via the existing /api/sheets/:id/pdf endpoint
//   - Render the crop region via pdf.js with the offsetX/offsetY trick
//   - Rotate for vertical-up / vertical-down orientations
//   - Otsu binarize for clean character edges
//   - Run Tesseract recognize with region-specific PSM + whitelist
//
// Workers are pooled and reused across sheets — createWorker is slow
// (downloads traineddata + WASM core), so we initialize once on first use
// and keep them warm for the rest of the OCR run.

import * as pdfjsLib from 'pdfjs-dist';
import { createWorker } from 'tesseract.js';
import { getSheetPdfUrl } from './api';

// Wire pdfjs's worker bootstrap if no other component has already done it.
// Idempotent — components in the viewer/templates already do the same thing.
if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url
  ).toString();
}

// ─── Region tuning ───────────────────────────────────────────────────────────
// Mirrors server/routes/ocr.js REGION_CONFIGS. Single-line PSM + tight
// whitelist on numbers, uniform-block PSM on titles. Without these Tesseract
// falls back to defaults that are much worse on short, tightly-cropped text.
const REGION_CONFIGS = {
  number: {
    tessedit_pageseg_mode:     '7',
    tessedit_char_whitelist:   'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-/ ',
    preserve_interword_spaces: '0',
  },
  title: {
    tessedit_pageseg_mode:     '6',
    tessedit_char_whitelist:   '',
    preserve_interword_spaces: '1',
  },
};
const DEFAULT_PARAMS = {
  tessedit_pageseg_mode:     '3',
  tessedit_char_whitelist:   '',
  preserve_interword_spaces: '0',
};

// ─── Tesseract worker pool ───────────────────────────────────────────────────
// Browser Web Workers — one per pool slot. Cap at 8: empirically going
// higher caused per-render time to climb from ~2.5s to ~10s on a 16-core
// workstation because parallel pdf.js renders fight for the same CPU.
// Tesseract.recognize is also CPU-bound, so 8 workers + ~8 concurrent
// pdf.js renders is the natural ceiling on a typical CPU before
// contention dominates. hardwareConcurrency keeps it lower on
// tablets/phones automatically.
const POOL_SIZE = Math.min(
  8,
  Math.max(1, (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 2)
);

let poolReady = null;
let initListeners = [];   // resolve fns waiting for init to finish (for UI "preparing…" affordance)
const idleWorkers = [];
const waiters     = [];

function initPool() {
  if (poolReady) return poolReady;
  const tStart = performance.now();
  poolReady = (async () => {
    // Sequence init — concurrent createWorker calls each kick off a network
    // fetch for traineddata + worker script. On slow links, parallel inits
    // can stall behind each other; sequencing also lets early-arriving
    // recognize calls grab the first ready worker without waiting for the
    // whole pool.
    for (let i = 0; i < POOL_SIZE; i++) {
      const w = await createWorker('eng', 1); // 1 = OEM_LSTM_ONLY
      checkInWorker(w);
    }
    initListeners.forEach((cb) => cb());
    initListeners = [];
    // eslint-disable-next-line no-console
    console.log(
      `[ocr] tesseract worker pool initialized: ${POOL_SIZE} workers in ${Math.round(performance.now() - tStart)}ms ` +
      `(navigator.hardwareConcurrency=${(typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 'unknown'})`
    );
  })().catch((err) => {
    poolReady = null; // allow retry
    throw err;
  });
  return poolReady;
}

async function checkOutWorker() {
  await initPool();
  if (idleWorkers.length) return idleWorkers.shift();
  return new Promise((resolve) => waiters.push(resolve));
}

function checkInWorker(worker) {
  if (waiters.length) waiters.shift()(worker);
  else idleWorkers.push(worker);
}

async function withWorker(task) {
  const w = await checkOutWorker();
  inFlightWorkers++;
  if (inFlightWorkers > peakInFlight) peakInFlight = inFlightWorkers;
  try {
    return await task(w);
  } finally {
    inFlightWorkers--;
    checkInWorker(w);
  }
}

// Start pool initialization without awaiting completion — useful for
// pre-warming as soon as the user opens the OCR setup step. `onReady` is
// fired once the pool is fully initialized so the UI can swap the
// "preparing…" indicator off.
export function prewarmOcrEngine(onReady) {
  if (poolReady && idleWorkers.length === POOL_SIZE) {
    if (onReady) onReady();
    return;
  }
  if (onReady) initListeners.push(onReady);
  initPool().catch((err) => {
    console.warn('[ocrEngine] prewarm failed:', err);
  });
}

// ─── Otsu binarization ───────────────────────────────────────────────────────
// Mirrors server's binarizeInPlace + otsuThreshold. Hard B&W gives Tesseract
// clean character edges and is the single biggest accuracy lever for the
// classic S↔5 / B↔8 / O↔0 confusions on rendered vector text.
function otsuThreshold(imageData) {
  const d = imageData.data;
  const hist = new Array(256).fill(0);
  let total = 0;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.round(d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114);
    hist[v]++;
    total++;
  }
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, maxVar = 0, threshold = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > maxVar) { maxVar = between; threshold = t; }
  }
  return threshold;
}

function binarizeInPlace(canvas) {
  const ctx = canvas.getContext('2d');
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const t = otsuThreshold(img);
  for (let i = 0; i < d.length; i += 4) {
    const v = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
    const b = v < t ? 0 : 255;
    d[i] = b; d[i + 1] = b; d[i + 2] = b;
  }
  ctx.putImageData(img, 0, 0);
}

// ─── Diagnostics ─────────────────────────────────────────────────────────────
// Per-phase timing + heap snapshot, logged once per sheet so we can see where
// the bottleneck actually lives. performance.memory is Chrome / Edge only;
// other browsers just get timings without heap info.
function memSnapshot() {
  if (typeof performance === 'undefined' || !performance.memory) return null;
  const m = performance.memory;
  return {
    usedMB:  Math.round(m.usedJSHeapSize  / 1048576),
    totalMB: Math.round(m.totalJSHeapSize / 1048576),
    limitMB: Math.round(m.jsHeapSizeLimit / 1048576),
  };
}

// In-flight stats — for "how many workers are actually busy?"
let inFlightWorkers = 0;
let peakInFlight    = 0;

// ─── Sheet PDF cache ─────────────────────────────────────────────────────────
// pdf.js getDocument() over the network is the slowest single step in the
// pipeline (HTTP fetch + parse). Cache loaded docs in-memory keyed by sheet
// id so back-to-back regions on the same sheet don't re-fetch. The cache is
// short-lived: we destroy entries when releaseSheet() is called after all
// regions have been processed.
const sheetDocCache = new Map(); // sheetId -> Promise<PDFDocumentProxy>

function loadSheetDoc(sheetId) {
  if (sheetDocCache.has(sheetId)) return sheetDocCache.get(sheetId);
  const url = getSheetPdfUrl(sheetId);
  // pdfjsLib.getDocument internally fetches + parses. We split timing into
  // "fetch+parse combined" since pdfjs doesn't expose a finer breakdown
  // without monkey-patching its loading task.
  const p = pdfjsLib.getDocument(url).promise.catch((err) => {
    sheetDocCache.delete(sheetId);
    throw err;
  });
  sheetDocCache.set(sheetId, p);
  return p;
}

async function releaseSheet(sheetId) {
  const p = sheetDocCache.get(sheetId);
  if (!p) return;
  sheetDocCache.delete(sheetId);
  try {
    const doc = await p;
    await doc.destroy();
  } catch (_) { /* swallow — best-effort cleanup */ }
}

// ─── Public: pre-fetch sheet PDFs (small concurrency, sliding window) ────────
// First experiment fired 16 parallel fetches up front + kept all docs cached
// for the duration of the run — that produced 3.3 GB tab memory pressure AND
// catastrophic per-fetch slowdown because the Render server's egress
// bandwidth got carved into 16 tiny slices, ballooning per-fetch time from
// ~5 sec to ~20 sec. Net wall time got *worse*.
//
// Now: small fixed concurrency (default 4), each prefetch worker walks the
// sheet list one item at a time. The worker loop's runOcrOnSheet awaits the
// cached promise, so by the time it gets to a sheet, fetch+parse is usually
// near-zero. As soon as the OCR loop releases a doc (via destroy() in its
// finally), browser memory drops, so total resident pdf.js doc memory stays
// bounded by ~(prefetch_concurrency + ocr_concurrency) docs at once.
//
// Fire-and-forget — errors here are swallowed; the actual run will surface
// real failures via runOcrOnSheet's normal path.
export function prefetchSheetDocs(sheetIds, concurrency = 4) {
  let idx = 0;
  async function worker() {
    while (idx < sheetIds.length) {
      const i = idx++;
      try { await loadSheetDoc(sheetIds[i]); }
      catch (_) { /* surfaced by the actual OCR run */ }
    }
  }
  Promise.all(Array.from({ length: Math.min(concurrency, sheetIds.length) }, worker))
    .catch(() => {});
}

// ─── Public: OCR a single sheet across multiple regions ──────────────────────
// regions: [{ crop_rect: {x,y,w,h}, orientation, region: 'number'|'title' }]
// Returns { results: [{ region, text, raw_text, confidence, words, crop_size, ms }] }
// Same shape as the server's /api/ocr/crop-multi response.
export async function runOcrOnSheet(sheetId, pageNum, regions, scale = 2.5) {
  // Per-sheet phase timing for the diagnostic log line at the bottom. All
  // values are ms cumulative across the regions array.
  const t = { fetch: 0, render: 0, ocr: 0 };
  const tStart = performance.now();

  const tFetch0 = performance.now();
  const doc = await loadSheetDoc(sheetId);
  t.fetch = performance.now() - tFetch0;

  try {
    if (pageNum < 1 || pageNum > doc.numPages) {
      throw new Error(`page_number out of range (pdf has ${doc.numPages} pages)`);
    }

    const page    = await doc.getPage(pageNum);
    const fullVp  = page.getViewport({ scale });
    const out     = [];

    for (const reg of regions) {
      const { crop_rect, orientation = 'horizontal', region: regionName } = reg;
      const { x, y, w, h } = crop_rect;
      const cropW = Math.round(fullVp.width  * w);
      const cropH = Math.round(fullVp.height * h);
      const cropX = Math.round(fullVp.width  * x);
      const cropY = Math.round(fullVp.height * y);
      const vp    = page.getViewport({ scale, offsetX: -cropX, offsetY: -cropY });

      const tRender0 = performance.now();
      // Off-screen canvas; never attached to DOM.
      const cropCanvas = document.createElement('canvas');
      cropCanvas.width  = cropW;
      cropCanvas.height = cropH;
      const cropCtx = cropCanvas.getContext('2d');
      cropCtx.fillStyle = '#ffffff';
      cropCtx.fillRect(0, 0, cropW, cropH);
      await page.render({ canvasContext: cropCtx, viewport: vp }).promise;

      // Capture the pre-rotation, pre-binarization crop as a data URL so the
      // REVIEW step can show the user exactly what OCR was looking at.
      // Pre-rotation = matches how the region appears on the original page,
      // which is the most intuitive view for spot-checking OCR errors.
      // ~10-50 KB per region as PNG; 78 thumbs (39 sheets × 2 regions) ~=
      // 1-3 MB sitting in React state during REVIEW. Acceptable.
      const cropDataUrl = cropCanvas.toDataURL('image/png');

      let ocrCanvas = cropCanvas;
      if (orientation === 'vertical-up' || orientation === 'vertical-down') {
        ocrCanvas = document.createElement('canvas');
        ocrCanvas.width  = cropH;
        ocrCanvas.height = cropW;
        const rctx = ocrCanvas.getContext('2d');
        rctx.fillStyle = '#ffffff';
        rctx.fillRect(0, 0, cropH, cropW);
        if (orientation === 'vertical-up') {
          rctx.translate(cropH, 0);
          rctx.rotate(Math.PI / 2);
        } else {
          rctx.translate(0, cropW);
          rctx.rotate(-Math.PI / 2);
        }
        rctx.drawImage(cropCanvas, 0, 0);
      }

      binarizeInPlace(ocrCanvas);
      t.render += performance.now() - tRender0;

      const params = REGION_CONFIGS[regionName] || DEFAULT_PARAMS;
      const tOcr0 = performance.now();
      const { data: ocr } = await withWorker(async (worker) => {
        await worker.setParameters(params);
        return worker.recognize(ocrCanvas);
      });
      const regionOcrMs = Math.round(performance.now() - tOcr0);
      t.ocr += regionOcrMs;

      out.push({
        region:         regionName || null,
        text:           (ocr.text || '').trim(),
        raw_text:       ocr.text || null,
        corrected_from: null, // discipline-aware correction is not done client-side yet
        confidence:     ocr.confidence,
        words: (ocr.words || []).map((wd) => ({
          text:       wd.text,
          confidence: wd.confidence,
          bbox:       wd.bbox,
        })),
        crop_size:    { w: ocrCanvas.width, h: ocrCanvas.height },
        crop_dataurl: cropDataUrl,
        ms: regionOcrMs,
      });
    }

    const totalMs = Math.round(performance.now() - tStart);
    const mem = memSnapshot();
    const memStr = mem ? ` heap=${mem.usedMB}/${mem.totalMB}MB (limit ${mem.limitMB})` : '';
    // Compact one-liner per sheet — paste a chunk of these to me and I can
    // tell you which phase is dominating, whether workers are saturated,
    // and whether memory is climbing.
    // eslint-disable-next-line no-console
    console.log(
      `[ocr] sheet=${sheetId.slice(0, 8)} ` +
      `total=${totalMs}ms fetch+parse=${Math.round(t.fetch)}ms ` +
      `render+rot+bin=${Math.round(t.render)}ms ocr=${Math.round(t.ocr)}ms ` +
      `pool=${inFlightWorkers}/${POOL_SIZE} (peak ${peakInFlight})${memStr}`
    );

    return { results: out };
  } finally {
    // Drop the cached doc + page caches as soon as the sheet's done being
    // worked on. Without this a 39-sheet run keeps all 39 pdfjs docs alive
    // in browser memory for the duration of the run.
    await releaseSheet(sheetId);
  }
}
